<template>
  <div class="w-full">
    <Pie
      :data="chartData"
      :options="chartOptions"
      :chart-id="chartId"
      :dataset-id-key="datasetIdKey"
      :plugins="plugins"
      :css-classes="cssClasses"
      :styles="styles"
      :width="width"
      :height="height"
    />
  </div>
</template>

<script setup lang="ts">
import { safeDenominator } from '@/helpers';
import { t } from '@/i18n';
import VariationOrder, { VariationOrderStatusColours, VariationOrderStatusTitles } from '@/models/VariationOrder';
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from 'chart.js';
import { computed } from 'vue';
import { Pie } from 'vue-chartjs';

// Register ChartJS components
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
);

const props = defineProps<{
  variationOrdersByStatus: Map<string, VariationOrder[]>;
  variationsSummary: VariationOrdersSummary;
}>();

// Chart configuration
const chartId = 'variations-status-chart';
const datasetIdKey = 'label';
const plugins = [];
const cssClasses = '';
const styles = {};
const width = undefined;
const height = undefined;

const chartData = computed(() => {
  const variations = Array.from(props.variationOrdersByStatus.keys());

  const variationsPerStatus = Array.from(props.variationOrdersByStatus.values()).map((variations) => {
    return variations.length;
  });

  const statusLabels = variations.map((status) => {
    return t(`Models.VariationOrder.statusTitles.${status}`);
  });

  const colours = variations.map((status) => {
    return VariationOrderStatusColours[status];
  });

  return {
    labels: statusLabels,
    datasets: [{
      data: variationsPerStatus,
      backgroundColor: colours,
      hoverOffset: 4,
    }],
  };
});

const chartOptions = {
  responsive: true,
  plugins: {
    legend: {
      position: 'bottom' as const,
    },

    tooltip: {
      callbacks: {
        label(this: any, tooltipItem: any) {
          return `${tooltipItem.dataset.data[tooltipItem.dataIndex]} (${
            (tooltipItem.dataset.data[tooltipItem.dataIndex] /
              safeDenominator(props.variationsSummary.total_variations) * 100)
              .toFixed(2)
          }%)`;
        },
      },
    },
  },

  maintainAspectRatio: false,
};
</script>

<style scoped></style>
